import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { ALERT_TYPE } from 'src/app/constants';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Company } from 'src/model/company.model';
import { TxSet } from 'src/model/set.model';
import { Transaction } from 'src/model/transaction.model';
import { TransactionSubType } from 'src/model/transactionSubType.model';
import { TransactionType } from 'src/model/transactionType.model';
import { CompanyService } from 'src/services/company.service';
import { SetService, TxSetOptions } from 'src/services/set.service';
import { TransactionService } from 'src/services/transaction.service';
import { AddReceiptSetComponent } from '../add-receipt-set/add-receipt-set.component';
import { ShowUnprocessedSetTransactionComponent } from '../show-unprocessed-set-transaction/show-unprocessed-set-transaction.component';

@Component({
    selector: 'app-receipt-sets-list',
    templateUrl: './receipt-sets-list.component.html',
    styleUrls: ['./receipt-sets-list.component.scss'],
    standalone: false
})
export class ReceiptSetsListComponent implements OnInit {
  protected sharedService = inject(SharedService);
  private restapi = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  protected enums = inject(Enums);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private companyService = inject(CompanyService);
  private setService = inject(SetService);
  private transService = inject(TransactionService);

  // selectedCompanyCode: string;
  myControl = new UntypedFormControl();
  filteredCompany: Observable<Company[]>;
  companyList: Company[] = [];
  startDate: Date;
  endDate: Date;
  selectedCompany: number;
  transactionList: Transaction[] = [];

  setList: TxSet[] = [];
  wholeSetList: TxSet[] = [];

  routeSubscription: any;
  showValid = false;
  filterTransactionTypeList: TransactionType[] = [];
  filteredType: number;

  isListView: boolean;
  showBookmark: boolean;
  expenseSubtypeList: TransactionSubType[] = [];
  dlSubtypeList: TransactionSubType[] = [];
  filteredSubtypeList: TransactionSubType[] = [];
  filteredSubtype: number;

  searchText: string;
  disableSubmit = false;
  latestCheckInDate: Date = new Date();

  adminView = false;
  searching = false;

  constructor() {
    //filter company
    this.filteredCompany = this.myControl.valueChanges.pipe(
      startWith(''),
      map(value => {if (typeof value === 'number') {
          const comp = this.companyList?.find(c => c.ID === value);
          return comp ? comp.CompanyCode : '';
        }
        return value || '';}),
      map(companyCode => this._filterCompanyCodeOptions(companyCode))
    );

    this.filterTransactionTypeList = [new TransactionType(this.enums.EXPENSE, "EXPENSE"), new TransactionType(this.enums.EXTRAS, "DUIT LEBIH"), new TransactionType(this.enums.ADVANCE, "ADVANCE")];
  }

  ngOnInit(): void {
    this.adminView = this.sharedService.isRoles([this.enums.ADMIN, this.enums.BOSS, this.enums.FINANCE, this.enums.EXPENSE_ADMIN]);
    this.isListView = sessionStorage.getItem("setListView") !== null ? sessionStorage.getItem("setListView") === 'true' : this.sharedService.isExpenseAdmin();
    this.activatedRoute.paramMap.pipe(
      tap(params => {
        const comp = params.get('companyId');
        this.selectedCompany = comp === undefined ? undefined : +comp;

        const startDate = params.get('startDate');
        this.startDate = startDate ? new Date(startDate) : undefined;

        const endDate = params.get('endDate');
        this.endDate = endDate ? new Date(endDate) : undefined;

        this.filteredType = +(params.get('type') ?? 0);
        this.filteredSubtype = +(params.get('subtype') ?? 0);
        this.showValid = params.get('showValid') === 'true';
        this.showBookmark = params.get('saved') === "true";
      }),
      switchMap(() => this.fetchData())
    ).subscribe();

    this.getSubTypeList();
  }

  async fetchData() {
    this.reset();
    if (this.sharedService.isBranchAccountHandler()) {
      this.selectedCompany = Number(sessionStorage.getItem("companyId"));
      this.startDate ??= await this.getCompanyLatestCheckIn(this.selectedCompany);
    }
    if (this.selectedCompany !== undefined) {
      if (this.adminView) {
        this.companyList = await this.companyService.getAllCompanies();
      }
      this.disableSubmit = false;
      this.setList = await this.getSetLists();
      this.setList.sort((a, b) => (a.Valid == undefined) ? -1 : 1);
      this.wholeSetList = Object.assign([], this.setList);
    }
  }

  getSetLists(): Promise<TxSet[]> {
    if (this.selectedCompany === undefined)
      return this.setService.getAllTxSet({ pending: true, priority: this.enums.FIRST_PRIORITY });
    else {
      const o: TxSetOptions = { type: this.filteredType, subtype: this.filteredSubtype, saved: this.showBookmark };
      if (this.sharedService.isBranchManager()) {
        let options: TxSetOptions;
        if (this.startDate !== undefined)
          options = { ...o, startDate: this.startDate, endDate: this.endDate, pending: true };
        else
          options = { ...o, valid: true, includePending: true };
        return Promise.all([
          this.setService.getAllCompanyTxSet(this.selectedCompany, options),
          this.setService.getAllCompanyTxSet(this.selectedCompany, { ...o, valid: false })
        ]).then(([r1, r2]) => [...r1, ...r2]);
      } else {
        return this.setService.getAllCompanyTxSet(this.selectedCompany, { ...o, startDate: this.startDate, endDate: this.endDate, valid: this.showValid, includePending: true });
      }
    }
  }

  async getSubTypeList() {
    [this.expenseSubtypeList, this.dlSubtypeList] = await Promise.all([
      this.transService.getTransactionSubTypes(this.enums.EXPENSE),
      this.transService.getTransactionSubTypes(this.enums.EXTRAS),
    ]);
  }

  getCompanyLatestCheckIn(companyId: number) {
    return new Promise<Date>(resolve => {
      const xhttp = this.restapi.getRequest(this.constructApi.getCompaniesLatestCheckIns(companyId))
      xhttp.onreadystatechange = () => {
        if (xhttp.readyState == 4 && xhttp.status == 200) {
          const jsonArray = JSON.parse((xhttp.responseText));
          jsonArray.forEach(json => {
            resolve(new Date(json.date));
          });
        }
      }
    });
  }

  filterList() {
    if (this.searchText != "") {
      this.searching = true;
      this.setList = this.wholeSetList.filter((set: TxSet) => (set.Name.toUpperCase().includes(this.searchText.toUpperCase())) || Number(set.ID) == Number(this.searchText) || set.Remark.toString().toUpperCase().includes(this.searchText.toUpperCase()));
    } else {
      this.setList = this.wholeSetList;
      this.searching = false;
    }
  }

  changeListView() {
    sessionStorage.setItem("setListView", this.isListView.toString());
  }

  openAddNewSetModal() {
    const modalRef = this.sharedService.openModal(AddReceiptSetComponent, "largeModal");
  }

  changeFilteredSubtypeList(type: number) {
    if (type == this.enums.EXPENSE)
      this.filteredSubtypeList = this.expenseSubtypeList;
    else if (type == this.enums.EXTRAS)
      this.filteredSubtypeList = this.dlSubtypeList;
  }

  displayCompanyCode = (id: number): string => {
    const comp = this.companyList.find(c => c.ID === id);
    return comp ? comp.CompanyCode : '';
  }

  private _filterCompanyCodeOptions(value: string): Company[] {
    const filterValue = value.toLowerCase();
    const filtered = this.companyList.filter(company => company.CompanyCode.toLowerCase().includes(filterValue));
    return filtered;
  }

  goToCompanySet() {
    this.disableSubmit = false;
    if (this.startDate !== undefined && this.endDate !== undefined)
      this.router.navigate(['/receiptList', this.selectedCompany, this.startDate.toISOString(), this.endDate.toISOString(), this.showValid, this.filteredType, this.filteredSubtype, this.showBookmark]);
    else if (this.startDate !== undefined || this.endDate !== undefined)
      this.sharedService.openAlert("Please select both start date and end date!", ALERT_TYPE.DANGER);
    else
      this.router.navigate(['/receiptList', this.selectedCompany, this.showValid, this.filteredType, this.filteredSubtype, this.showBookmark]);
  }

  showUnprocessedTransaction() {
    if (this.startDate == undefined || this.selectedCompany == undefined)
      this.sharedService.openAlert("Please select company and start date", ALERT_TYPE.DANGER);
    else {
      const modalRef = this.sharedService.openModal(ShowUnprocessedSetTransactionComponent, "largeModal");
      modalRef.componentInstance.selectedCompany = this.selectedCompany;
      modalRef.componentInstance.startDate = this.startDate;
    }
  }

  reset() {
    this.setList.length = 0;
    this.wholeSetList.length = 0;
  }

  reload() {
    if (typeof window !== 'undefined') { window.location.reload(); }
  }
}
